import { useState } from "react";
import { Segments } from "@core/components/Segments";
import { ValueRow } from "@core/components/Value";
import { useSm, type Crafter, type InventoryView } from "./view";

const grades = ["ohne Meisterschaft", "Geselle", "Fachmann", "Meister"];

type Who = "unknown" | "self" | "other";

/**
 * Who made a piece (MSK p. 84–85, 94, 99; SM-G-131): the character himself, whose crafting masteries count and for whom difficulty,
 * progress points and material cost are computed, or another maker with the grade and masteries the user names. Unknown is the default
 * for bought goods; then going beyond the usual limits is only a warning.
 */
export function CrafterFields({ item, crafter, onChange }: { item: InventoryView; crafter: Crafter | null; onChange: (crafter: Crafter | null) => void }) {
  const { view } = useSm();
  const who: Who = crafter === null ? "unknown" : crafter.self ? "self" : "other";
  const base: Crafter = crafter ?? { self: false, masteries: [], ownMaterial: false };
  const update = (change: Partial<Crafter>) => onChange({ ...base, ...change });
  const craft = item.craftKeys;
  return (
    <section className="editor-block stack small-gap" aria-label="Herstellung">
      <strong>Hergestellt von</strong>
      <Segments<Who>
        ariaLabel="Hergestellt von"
        value={who}
        options={[
          { value: "unknown", label: "unbekannt" },
          { value: "self", label: "selbst" },
          { value: "other", label: "Handwerker" },
        ]}
        onChange={(next) => onChange(next === "unknown" ? null : { ...base, self: next === "self", grade: next === "self" ? null : base.grade ?? 1 })}
      />
      {who === "self" && (
        <>
          <div className="inline-form two">
            <label className="field">
              <span>Fertigkeit</span>
              <select value={base.skill ?? ""} onChange={(e) => update({ skill: e.target.value || null })}>
                <option value="">nicht festgelegt</option>
                {view.crafting.skills.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Schwerpunkt</span>
              <input value={base.focus ?? ""} placeholder="z. B. Schmiedekunst" onChange={(e) => update({ focus: e.target.value || null })} />
            </label>
          </div>
          <label className="option">
            <input type="checkbox" checked={base.ownMaterial} onChange={(e) => update({ ownMaterial: e.target.checked })} />
            <span>alles Material vorhanden: nur ein Zehntel des Preises (Mondstahlklingen S. 95)</span>
          </label>
        </>
      )}
      {who === "other" && (
        <>
          <label className="field">
            <span>Stufe des Handwerkers</span>
            <select value={base.grade ?? 0} onChange={(e) => update({ grade: Number(e.target.value) })}>
              {grades.map((g, index) => (
                <option key={g} value={index}>
                  {g}
                </option>
              ))}
            </select>
          </label>
          <div className="stack small-gap" role="group" aria-label="Meisterschaften des Handwerkers">
            {view.crafting.masteries.map((m) => (
              <label key={m.id} className="option">
                <input
                  type="checkbox"
                  checked={base.masteries.includes(m.id)}
                  onChange={(e) => update({ masteries: e.target.checked ? [...base.masteries, m.id] : base.masteries.filter((x) => x !== m.id) })}
                />
                <span>{m.name}</span>
              </label>
            ))}
          </div>
        </>
      )}
      {item.maxQualityKey && <ValueRow valueKey={item.maxQualityKey} label="Höchstqualität" />}
      {craft?.schwierigkeit && <ValueRow valueKey={craft.schwierigkeit} label="Schwierigkeit der Herstellung" />}
      {craft?.fortschritt && <ValueRow valueKey={craft.fortschritt} label="Fortschrittspunkte" />}
      {craft?.stunden && <ValueRow valueKey={craft.stunden} label="Stunden je Probe" />}
      {craft?.material && <ValueRow valueKey={craft.material} label="Materialkosten" />}
    </section>
  );
}

/**
 * Workshops at the refuges of the character (MSK p. 87; SM-G-146): a refuge holds one when a crafting skill and its focus are named;
 * its level gives a bonus on that focus and lowers the cost of pieces the character makes himself.
 */
export function Workshops() {
  const { view, apply } = useSm();
  const resource = view.crafting.workshopResource;
  const refuges = view.resources.filter((r) => resource && r.id === resource);
  if (refuges.length === 0) return null;
  return (
    <div className="stack small-gap" role="group" aria-label="Werkstätten">
      <strong>Werkstatt</strong>
      {refuges.map((r) => (
        <WorkshopRow key={r.key} resourceKey={r.key} label={r.param ? `${r.name} (${r.param})` : r.name} current={view.crafting.workshops[r.key] ?? null} skills={view.crafting.skills} onSave={(skill, focus) => apply({ type: "sm.setWorkshop", payload: { key: r.key, skill, focus } })} />
      ))}
    </div>
  );
}

function WorkshopRow(props: { resourceKey: string; label: string; current: { skill: string; name: string } | null; skills: { id: string; name: string }[]; onSave: (skill: string | null, focus: string | null) => void }) {
  const [focus, setFocus] = useState(props.current?.name ?? "");
  const skill = props.current?.skill ?? "";
  return (
    <div className="inline-form two">
      <label className="field">
        <span>{props.label}</span>
        <select aria-label={`Werkstatt ${props.label}`} value={skill} onChange={(e) => props.onSave(e.target.value || null, focus.trim() || null)}>
          <option value="">keine Werkstatt</option>
          {props.skills.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      {skill && (
        <label className="field">
          <span>Schwerpunkt</span>
          <input
            aria-label={`Schwerpunkt der Werkstatt ${props.label}`}
            value={focus}
            placeholder="z. B. Schmiedekunst"
            onChange={(e) => setFocus(e.target.value)}
            onBlur={() => focus.trim() !== (props.current?.name ?? "") && props.onSave(skill, focus.trim() || null)}
          />
        </label>
      )}
    </div>
  );
}
